import { Injectable, inject } from '@angular/core';
import {
    GenericConfirmationDialogComponent,
    GenericConfirmationDialogData,
} from '@r1-compass-library/components/dialog/generic-confirmation-dialog/generic-confirmation-dialog.component';
import { ContentAsset, LifecycleRuleset, Play, TemplateCriteria } from '@r1-compass-library/models';
import { PlayService } from '@r1-compass-library/services';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';

type AssetInUseByPlay = ContentAsset | LifecycleRuleset | TemplateCriteria;
type AssetType = 'lifecycle rule' | 'template criteria' | 'content asset';

@Injectable({ providedIn: 'root' })
export class AssetInvalidSaveWarningService {
    readonly #dialog = inject(MatDialog);
    readonly #playService = inject(PlayService);

    async confirmSave(assetType: AssetType, asset: Partial<AssetInUseByPlay>): Promise<boolean> {
        if (!asset.id || !asset.enabled || asset.valid !== false) {
            return true;
        }

        const search =
            assetType === 'lifecycle rule'
                ? { lifecycleRulesetId: asset.id, deleted: 'False' as const }
                : assetType === 'template criteria'
                  ? { templateCriteriaId: asset.id, deleted: 'False' as const }
                  : { contentAssetId: asset.id, deleted: 'False' as const };
        const plays = await firstValueFrom(this.#playService.search(search));
        if (!plays.length) {
            return true;
        }

        const playNames = plays.map((play: Play) => play.name).join(', ');
        const dialogRef = this.#dialog.open<GenericConfirmationDialogComponent, GenericConfirmationDialogData>(
            GenericConfirmationDialogComponent,
            {
                disableClose: true,
                autoFocus: false,
                data: {
                    message: `You are attempting to save an invalid ${assetType} in use by the following plays: ${playNames}. This may cause these plays to not execute as expected. Are you sure?`,
                    confirmButton: { text: 'Save', type: 'stroked' },
                    cancelButton: { text: 'Cancel', type: 'outlined', color: 'var(--r1-color-brilliant-blue)' },
                },
            }
        );
        return Boolean(await firstValueFrom(dialogRef.afterClosed()));
    }
}
